import { useState, useEffect } from "react";
import { useNotificationStore } from "@/zustand/notificationsStore";
import CheckCircleIcon from "../OpticksIcons/CheckCircleIcon";
import AlertTriangleIcon from "../OpticksIcons/AlertTriangleIcon";
import AlertCircleIcon from "../OpticksIcons/AlertCircleIcon";
import InfoCircleIcon from "../OpticksIcons/InfoCircleIcon";
import OpticksButton from "../OpticksButton/OpticksButton";
import CloseIcon from "../OpticksIcons/CloseIcon";

const OpticksNotificationsItem = ({
  notification,
}: {
  notification: {
    id: string;
    type: "info" | "success" | "error" | "warning" | "loading";
    message: string;
    title?: string;
    duration: number;
    dismissible?: boolean;
    pauseOnHover?: boolean;
  };
}) => {
  const [isVisible, setIsVisible] = useState(false);
  const [isExiting, setIsExiting] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const { removeNotification, pauseNotification, resumeNotification } =
    useNotificationStore();

  useEffect(() => {
    // Trigger enter animation
    const timer = setTimeout(() => setIsVisible(true), 10);
    return () => clearTimeout(timer);
  }, []);

  const handleClose = () => {
    setIsExiting(true);
    setTimeout(() => {
      removeNotification(notification.id);
    }, 300); // Match CSS animation duration
  };

  const handleMouseEnter = () => {
    if (notification.pauseOnHover && !isPaused) {
      setIsPaused(true);
      pauseNotification(notification.id);
    }
  };

  const handleMouseLeave = () => {
    if (notification.pauseOnHover && isPaused) {
      setIsPaused(false);
      resumeNotification(notification.id);
    }
  };

  const getIcon = () => {
    switch (notification.type) {
      case "success":
        return <CheckCircleIcon className="icon-traffic-legitimate-darker" />;
      case "error":
        return <AlertTriangleIcon className="icon-traffic-invalid-darker" />;
      case "warning":
        return <AlertCircleIcon className="icon-traffic-suspicious-dark" />;
      case "info":
        return <InfoCircleIcon className="icon-deep-blue" />;
      default:
        return <InfoCircleIcon />;
    }
  };

  const getProgressBar = () => {
    if (notification.duration <= 0) return null;

    return (
      <div className="notification-progress">
        <div
          className="notification-progress-bar"
          style={{
            animationDuration: `${notification.duration}ms`,
            animationPlayState: isPaused ? "paused" : "running",
          }}
        />
      </div>
    );
  };

  return (
    <div
      className={`notification-item notification-${notification.type} ${isVisible && !isExiting ? "notification-visible" : ""} ${isExiting ? "notification-exiting" : ""} ${isPaused ? "notification-paused" : ""}`}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      role="alert"
      aria-live="polite"
    >
      <div className="notification-icon">{getIcon()}</div>

      <div className="notification-content">
        {notification.title && (
          <b className="notification-title">{notification.title}</b>
        )}
        <b className="notification-message">{notification.message}</b>
      </div>

      {notification.dismissible && (
        <OpticksButton
          onClick={handleClose}
          icon={<CloseIcon />}
          className="notification-close"
          aria-label="Close notification"
        />
      )}

      {getProgressBar()}
    </div>
  );
};

export default OpticksNotificationsItem;
